import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import Spinner from './Spinner.js';
import { isAdminUser, isOpsManager } from './accessControl.js';
import StatusBadge from './components/StatusBadge.js';
import { updateDispatchAudit } from './utils/dispatchAudit.js';

const MaterialMovement = ({ db, user }) => {
  const navigate = useNavigate();
  const [mocs, setMocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterWarehouse, setFilterWarehouse] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const q = query(collection(db, "dispatches"), orderBy("updatedAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const mocList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMocs(mocList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const warehouseOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.warehouse).filter(Boolean)))], [mocs]);
  const typeOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.typeOfMOC).filter(Boolean)))], [mocs]);
  const statusOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.dispatchStatus).filter(Boolean)))], [mocs]);

  const filteredMocs = useMemo(() => {
    return mocs.filter(moc => {
      if (moc.dispatchStatus === 'delivered') return false;
      
      const matchesWarehouse = filterWarehouse === 'All' || moc.warehouse === filterWarehouse;
      const matchesType = filterType === 'All' || moc.typeOfMOC === filterType;
      const matchesStatus = filterStatus === 'All' || moc.dispatchStatus === filterStatus;
      const mocDate = moc.mocDate || moc.dispatchDate || '';
      const matchesDateFrom = !dateFrom || (mocDate && mocDate >= dateFrom);
      const matchesDateTo = !dateTo || (mocDate && mocDate <= dateTo);

      return matchesWarehouse && matchesType && matchesStatus && matchesDateFrom && matchesDateTo;
    });
  }, [mocs, filterWarehouse, filterType, filterStatus, dateFrom, dateTo]);

  const queueForPacking = async (moc) => {
    const confirmed = window.confirm('Move this MOC to Packing Module?');
    if (!confirmed) return;

    try {
      const timestamp = serverTimestamp();
      const auditTimestamp = new Date().toISOString();
      await updateDoc(doc(db, 'dispatches', moc.id), {
        dispatchStatus: 'packing_queued',
        updatedAt: timestamp
      });
      await updateDispatchAudit(db, {
        dispatchId: moc.id,
        dispatchData: {
          ...moc,
          dispatchStatus: 'packing_queued'
        },
        user,
        historyEvent: {
          action: 'packing_queued',
          dispatchStatus: 'packing_queued',
          timestamp: auditTimestamp
        }
      });

      if (moc.orderId) {
        await updateDoc(doc(db, 'orders', moc.orderId), {
          status: 'packing_queued',
          updatedAt: timestamp
        });
      }

      navigate('/ops/packing');
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const getActionConfig = (moc) => {
    const { dispatchStatus, id: mocId } = moc;

    if (isAdminUser(user)) {
      return {
        label: 'Preview MOC',
        variant: 'preview',
        onClick: () => navigate(`/ops/moc/preview/${mocId}`)
      };
    }

    if (dispatchStatus === 'moc_approved') {
      return {
        label: 'Queue for Packing',
        variant: 'success',
        onClick: () => queueForPacking(moc)
      };
    }

    if (isOpsManager(user)) {
      if (dispatchStatus === 'moc_draft') {
        return {
          label: 'Edit MOC',
          variant: 'success',
          onClick: () => navigate(`/ops/moc/edit/${mocId}`)
        };
      }

      if (dispatchStatus === 'pending_ops_approval') {
        return {
          label: 'Review MOC',
          variant: 'success',
          onClick: () => navigate(`/ops/moc/preview/${mocId}`)
        };
      }

      return {
        label: 'Preview MOC',
        variant: 'preview',
        onClick: () => navigate(`/ops/moc/preview/${mocId}`)
      };
    }

    if (['moc_draft', 'moc_rejected'].includes(dispatchStatus)) {
      return {
        label: 'Edit MOC',
        variant: 'success',
        onClick: () => navigate(`/ops/moc/edit/${mocId}`)
      };
    }

    return {
      label: 'Preview MOC',
      variant: 'preview',
      onClick: () => navigate(`/ops/moc/preview/${mocId}`)
    };
  };

  const getStatusBadge = (status) => {
    switch (status) {
        case 'moc_draft':
            return <StatusBadge status="moc_draft" iconSize={12} />;
        case 'packed_ready':
            return <StatusBadge status="packed_ready" iconSize={12} />;
        case 'invoice_pending':
            return <StatusBadge status="invoice_pending" iconSize={12} />;
        case 'ewaybill_pending':
            return <StatusBadge status="ewaybill_pending" iconSize={12} />;
        case 'invoice_ready':
            return <StatusBadge status="invoice_ready" iconSize={12} />;
        case 'ewaybill_complete':
            return <StatusBadge status="ewaybill_complete" iconSize={12} />;
        case 'dc_confirmed':
        case 'qc_pending':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#166534' }}>
                <CheckCircle size={12}/> INVOICE READY
            </span>;
        case 'qc_passed':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#166534' }}>
                <CheckCircle size={12}/> QC PASSED
            </span>;
        case 'dispatched':
            return <StatusBadge status="dispatched" iconSize={12} />;
        case 'moc_approved':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#166534' }}>
                <CheckCircle size={12}/> MOC APPROVED
            </span>;
        case 'moc_pending_finance':
            return <StatusBadge status="moc_pending_finance" iconSize={12} />;
        case 'pending_ops_approval':
            return <StatusBadge status="pending_ops_approval" iconSize={12} />;
        case 'moc_rejected':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#fee2e2', color: '#b91c1c' }}>
                <AlertCircle size={12}/> MOC RETURNED
            </span>;
        default:
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#f3f4f6', color: '#64748b' }}>
                <Clock size={12}/> {status?.replace(/_/g, ' ') || 'DRAFT'}
            </span>;
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Bill Of Material</h2>
          <p style={{ margin: '5px 0 0', color: '#64748b', fontSize: '14px' }}>Manage shipment drafts and approvals.</p>
        </div>
        {!isAdminUser(user) && (
          <button 
            onClick={() => navigate('/ops/moc/new')} 
            style={createBtnStyle}
          >
            <Plus size={18} /> {isMobile ? 'New' : 'Create MOC'}
          </button>
        )}
      </div>

      {/* FILTERS */}
      <div style={filterSectionStyle}>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Warehouse</label>
          <select 
            value={filterWarehouse} 
            onChange={(e) => setFilterWarehouse(e.target.value)}
            style={filterInputStyle}
          >
            {warehouseOptions.map((option) => (
              <option key={option} value={option}>{option === 'All' ? 'All Warehouses' : option}</option>
            ))}
          </select>
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>MOC Type</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={filterInputStyle}
          >
            {typeOptions.map((option) => (
              <option key={option} value={option}>{option === 'All' ? 'All Types' : option}</option>
            ))}
          </select>
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={filterInputStyle}
          >
            {statusOptions.map((option) => (
              <option key={option} value={option}>{option === 'All' ? 'All Statuses' : option}</option>
            ))}
          </select>
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>From Date</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={filterInputStyle}
          />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>To Date</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={filterInputStyle}
          />
        </div>
      </div>

      {/* TABLE / LIST */}
      <div style={containerStyle}>
        {filteredMocs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            <FileText size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
            <p>No Bill Of Material found.</p>
          </div>
        ) : (
          <table style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>MOC No.</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredMocs.map((moc) => {
                const actionConfig = getActionConfig(moc);

                return (
                <tr 
                  key={moc.id} 
                  style={trStyle} 
                  onClick={() => navigate(`/ops/moc/preview/${moc.id}`)}
                >
                  <td style={tdStyle}>{moc.mocDate}</td>
                  <td style={{ ...tdStyle, fontWeight: 'bold', color: '#2563eb' }}>{moc.mocNo}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '600' }}>{moc.customerName}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{moc.materialCode}</div>
                  </td>
                  <td style={tdStyle}>{moc.typeOfMOC}</td>
                  <td style={tdStyle}>{moc.warehouse}</td>
                  <td style={tdStyle}>{getStatusBadge(moc.dispatchStatus)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        actionConfig.onClick();
                      }}
                      style={actionConfig.variant === 'success' ? successActionBtnStyle : actionBtnStyle}
                    >
                      {actionConfig.label}
                    </button>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// --- STYLES ---
const containerStyle = {
  backgroundColor: 'white',
  borderRadius: '10px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  overflow: 'hidden'
};

const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', minWidth: '150px', flex: '1' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const filterInputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };

const createBtnStyle = {
  backgroundColor: '#1e293b',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '8px',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const theadStyle = {
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0'
};

const thStyle = {
  padding: '12px 15px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 'bold',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const trStyle = {
  borderBottom: '1px solid #f1f5f9',
  cursor: 'pointer',
  transition: 'background-color 0.2s'
};

const tdStyle = {
  padding: '15px',
  fontSize: '14px',
  color: '#1e293b'
};

const actionBtnStyle = {
  backgroundColor: '#eff6ff',
  border: '1px solid #bfdbfe',
  padding: '8px 12px',
  color: '#1d4ed8',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: '700'
};

const successActionBtnStyle = {
  backgroundColor: '#059669',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '6px',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  cursor: 'pointer',
  fontSize: '12px'
};

export default MaterialMovement;
